import { createBigDecimal } from '@0xtorch/big-decimal'
import { z } from 'zod'

export const earnAssetSchema = z.object({
  /** coin | e.g. "USDT" */
  coin: z.string(),
  /** amount | e.g. "0" */
  amount: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
})

export const fundingAssetSchema = z.object({
  /** coin | e.g. "USDT" */
  coin: z.string(),
  /** available | e.g. "0" */
  available: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** frozen | e.g. "0" */
  frozen: z.union([
    z.literal('').transform(() => createBigDecimal('0')),
    z
      .string()
      .regex(/^\d+(\.\d+)?$/)
      .transform((v) => createBigDecimal(v)),
  ]),
  /** USDT value | e.g. "326" */
  usdtValue: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
})

export const futureAssetSchema = z.object({
  /** Margin coin | e.g. "USDT" */
  marginCoin: z.string(),
  /** Locked quantity (margin coin) | e.g. "0.31876482" */
  locked: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Available quantity in the account | e.g. "10575.26735771" */
  available: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Account equity (margin coin), Includes unrealized PnL (based on mark price) | e.g. "10582.90265771" */
  accountEquity: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Account equity in USDT | e.g. "10582.902657719473" */
  usdtEquity: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Account equity in BTC | e.g. "0.204885807029" */
  btcEquity: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Risk ratio in cross margin mode | e.g. "0" */
  crossedRiskRate: z.string(),
  /** Unrealized PnL (margin coin) | e.g. "" */
  unrealizedPL: z.union([
    z.literal('').transform(() => createBigDecimal('0')),
    z
      .string()
      .regex(/^\d+(\.\d+)?$/)
      .transform((v) => createBigDecimal(v)),
  ]),
  /** Trading bonus | e.g. "0" */
  coupon: z.string(),
})

export const marginAssetSchema = z.object({
  /** Token name | e.g. "USDT" */
  coin: z.string(),
  /** Total amount | e.g. "12" */
  totalAmount: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Available amount | e.g. "2" */
  available: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Assets frozen | e.g. "0" */
  frozen: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Borrow | e.g. "0.1" */
  borrow: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Interest, Interest-only payments with a minimum payment of interest. | e.g. "0.000001" */
  interest: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Net assets = available + frozen − borrow − interest. Liquidation is triggered when the Maintenance Margin Ratio (MMR) is reached. | e.g. "0.1" */
  net: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Creation time | e.g. "1734567744432" */
  cTime: z.string().regex(/^\d+$/).transform(Number),
  /** Update time | e.g. "1734567744432" */
  uTime: z.string().regex(/^\d+$/).transform(Number),
  /** Trading bonus | e.g. "0" */
  coupon: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
})

export const isolatedMarginAssetSchema = marginAssetSchema.extend({
  /** Trading pair name | e.g. "BTCUSDT" */
  symbol: z.string(),
})

export const spotAssetSchema = z.object({
  /** Token name | e.g. "usdt" */
  coin: z.string(),
  /** Available assets | e.g. "0" */
  available: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Amount of frozen assets | e.g. "0" */
  frozen: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Amount of locked assets | e.g. "0" */
  locked: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** Update time(ms) | e.g. "1622697148" */
  uTime: z.string().regex(/^\d+$/).transform(Number),
})
